import clsx from 'clsx'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useState, useEffect, useContext } from 'react'
import { approveSubmittal, rejectSubmittal } from '@/services/submittalService'
import EFormRenderer from '@/components/EForm/EformRenderer'
import Loader from '@/components/Loader'
import AccordionCard from '@/components/AccordionCard'
import Button from '@/components/Button'
import ChevronLeft from '@/assets/svgs/chevron-left.svg?react'
import InfoIcon from '@/assets/svgs/information-circle.svg?react'
import { useSubmittalDetail } from '@/hooks/useSubmittalDetail'
import useListingTabPath from '@/hooks/useListingTabPath'
import useListingTabState from '@/hooks/useListingTabState'
import useModal from '@/hooks/useModal'
import usePage from '@/hooks/use-page'
import { SubmittalHeader } from './components/SubmittalHeader'
import { SubmittalBanner } from './components/SubmittalBanner'
import { ApproveModal } from './components/ApproveModal'
import { RejectModal } from './components/RejectModal'
import { ToastContext } from '@/context/ToastContext'
import useFeature, { features } from '@/hooks/useFeature'
import { SUBMISSION_MODE, SUBMITTAL_STATUS, approvalToast, rejectionToast } from './constants'
import { LISTING_PATH } from './listing-config'
import UploadedRowsTable from './components/UploadedRowsTable'
import { downloadSubmittalAsCsv } from './utils/downloadSubmittalAsCsv'

export const SubmittalDetailPage = () => {
  const { submittalId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { addToastMessage } = useContext(ToastContext)

  const listingTabPath = useListingTabPath()
  const listingTabState = useListingTabState()
  const { state, activityLogs, refetch } = useSubmittalDetail(submittalId)

  // The data element the row was opened from, or — for a page reached without one,
  // from an emailed link or a reload, since history state survives a reload but
  // not a fresh navigation — the one this submittal turns out to be listed on.
  // Undefined either way resolves to the bare listing.
  const activeDataElementId = location.state?.tabId || state.dataElementId
  const backTo = listingTabPath(activeDataElementId)

  // The crumb naming that listing, drawn only when there is a listing of its own
  // to name — with the flag off, or before the submittal has loaded, `backTo` is
  // the bare path and the trail stays the two crumbs it has always been.
  const listingCrumb =
    backTo !== LISTING_PATH && state.listingLabel
      ? { label: state.listingLabel, link: backTo }
      : null

  // A submittal opened cold says nowhere which data element it belongs to, so the
  // sidebar highlights none of them. The submittal itself is the answer once it
  // lands; written back onto this same history entry so the sidebar reads it the
  // way it reads every other arrival, and so a reload keeps it. Replace, not push:
  // the address is unchanged, and a second entry would make Back a no-op.
  useEffect(() => {
    if (location.state?.tabId) return
    // Undefined with the flag off, where there is one listing and nothing to
    // highlight — so that build writes no state it never reads.
    const tabState = listingTabState(state.dataElementId)
    if (!tabState) return
    navigate(`/submittals/${submittalId}`, {
      replace: true,
      state: { ...location.state, ...tabState },
    })
  }, [location.state, state.dataElementId, submittalId, navigate, listingTabState])

  // Called for the document title alone — the breadcrumbs are SubmittalHeader's.
  // It belongs here rather than in that header because the header only renders
  // once the submittal has loaded, so a page opened directly, still loading, or
  // errored would otherwise keep the title the listing left behind (or
  // index.html's, on a cold load).
  usePage({ title: 'Details' })

  // Gated on the flag as well as the config, mirroring the service, which ignores
  // the whole per-element record while the flag is off: an excel-upload record
  // authored in one environment's DynamoDB must not change another's behaviour.
  const isPhase2 = useFeature(features.SUBMITTALS_PHASE_2)
  const isExcelUpload = isPhase2 && state.submissionMode === SUBMISSION_MODE.EXCEL_UPLOAD

  // The mode the page's copy speaks in, behind that same gate: with the flag off
  // the service ignores the element's record, so the wording must not read from
  // it either and everything stays the E-Form phrasing EMS ships today.
  const messageMode = isExcelUpload ? SUBMISSION_MODE.EXCEL_UPLOAD : SUBMISSION_MODE.EFORM

  // The element's workflow, read through the same gate for the same reason, and
  // read once so the tag and the banner under it cannot disagree about the same
  // submittal — the contradiction this whole area exists to avoid. With the flag
  // off both fall back to single-stage wording, which is what EMS renders today.
  const secondaryApproval = isPhase2 ? state.secondaryApproval : undefined
  const [actionState, setActionState] = useState({ submitting: false })
  const [rejectReason, setRejectReason] = useState('')
  const [isDownloading, setDownloading] = useState(false)
  const {
    visible: approveModalVisible,
    show: showApproveModal,
    hide: hideApproveModal,
  } = useModal()
  const { visible: rejectModalVisible, show: showRejectModal, hide: hideRejectModal } = useModal()

  const handleApprove = async () => {
    hideApproveModal()
    setActionState({ submitting: true })
    try {
      await approveSubmittal(submittalId)
      addToastMessage(
        approvalToast({
          submissionMode: messageMode,
          receiverOrgName: state.receiverOrgName,
          secondaryApproval,
        }),
        'success',
        'dismiss'
      )
    } catch (err) {
      addToastMessage(err.response?.data?.detail || err.message, 'error', 'dismiss')
    } finally {
      refetch()
      setActionState({ submitting: false })
    }
  }

  const handleReject = async () => {
    hideRejectModal()
    setActionState({ submitting: true })
    try {
      await rejectSubmittal(submittalId, rejectReason)
      addToastMessage(
        rejectionToast({ submissionMode: messageMode, secondaryApproval }),
        'error',
        'dismiss'
      )
    } catch (err) {
      addToastMessage(err.response?.data?.detail || err.message, 'error', 'dismiss')
    } finally {
      refetch()
      setActionState({ submitting: false })
    }
  }

  const handleOpenReject = () => {
    setRejectReason('')
    showRejectModal()
  }

  const handleDownload = async () => {
    setDownloading(true)
    const { error } = await downloadSubmittalAsCsv(submittalId, state.listingLabel || 'submittal')
    setDownloading(false)
    if (error) addToastMessage(error, 'error', 'dismiss')
  }

  useEffect(() => {
    if (state.notFound) {
      addToastMessage(state.error || 'Submittal could not be found', 'error', 'dismiss')
      navigate(backTo, { replace: true })
    }
  }, [state.notFound])

  const isLoading = !state.error && (state.loading || !state.schema)

  return (
    <div>
      {!isLoading && !state.error && (
        <SubmittalHeader
          elementName={state.listingLabel}
          status={state.status}
          projectContractName={state.projectContractName}
          submissionMonth={state.submissionMonth}
          // Takes the month's slot as "Submission Date" when the submittal has
          // no month, which is how an upload element reads. Gated on the flag
          // rather than on `isExcelUpload` like `compactDates` below: this is not
          // a layout choice EMS has to be held back from but a rule about the
          // data, and the month test in the header already keeps every EMS page a
          // month page. Same condition pitstop applies.
          submissionDate={isPhase2 ? state.submissionDate : undefined}
          modified={state.modified}
          secondaryApproval={secondaryApproval}
          backTo={backTo}
          listingCrumb={listingCrumb}
          // This page serves EMS as well as the configured elements, and Download
          // is new here, so it is offered only for the submittals it was ported
          // for — behind the flag, and behind the element's own config. EMS keeps
          // the header it has in production. The DRAFT guard is pitstop's; a
          // consultant never sees a draft, so it is kept only so the two agree.
          onDownload={
            isExcelUpload && state.status !== SUBMITTAL_STATUS.DRAFT ? handleDownload : undefined
          }
          isDownloading={isDownloading}
          // pitstop's month and time formats, behind the same gate and for the
          // same reason as the Download button: EMS keeps the formats it renders
          // in production, which this change is not meant to touch.
          compactDates={isExcelUpload}
        />
      )}
      <div
        className={
          state.status !== SUBMITTAL_STATUS.PENDING_REVIEW
            ? 'phone:pb-24 pb-10'
            : 'phone:pb-35 pb-42'
        }
      >
        {isLoading && (
          <div className="flex items-center justify-center bg-white rounded-lg py-6 px-8 min-h-[calc(100vh-8rem)]">
            <Loader />
          </div>
        )}
        {!isLoading && state.error && (
          <div className="flex items-center justify-center bg-white rounded-lg py-6 px-8 min-h-[25rem]">
            <div className="flex flex-col items-center gap-4 text-center">
              <p className="type-body-2 text-grey-50">{state.error}</p>
              <Button sizing="Medium" round onClick={refetch}>
                Retry
              </Button>
            </div>
          </div>
        )}
        {!isLoading && !state.error && (
          <>
            <SubmittalBanner
              status={state.status}
              receiverOrgName={state.receiverOrgName}
              submissionMode={messageMode}
              secondaryApproval={secondaryApproval}
              activityLogs={activityLogs}
              // Same reasoning as Download above: the centred trigger is the
              // ported layout, so EMS keeps its top-aligned one.
              centerAction={isExcelUpload}
              compactDates={isExcelUpload}
            />
            {/* An uploaded submittal's payload is an array of row-records, a
                shape the eform cannot bind — it renders every field blank. Those
                rows go to a read-only table instead. */}
            {isExcelUpload ? (
              <UploadedRowsTable
                title={`${state?.listingLabel || 'Data'} Details`}
                payload={state.rawPayload}
                schema={state.schema}
              />
            ) : (
              <AccordionCard
                className="mt-6"
                contentClassName="max-h-[10000px]"
                title={
                  <div data-testid="eform-page-form-title" className="type-header-4 font-[900]">
                    {state?.listingLabel || 'Data'} Details
                  </div>
                }
                defaultOpen
              >
                <div className="flex flex-wrap items-start justify-between gap-y-6 type-body-2">
                  <EFormRenderer schema={state.schema} payload={state.payload} />
                </div>
              </AccordionCard>
            )}
            <div className="z-[1] flex flex-col fixed bottom-0 left-0 w-full ml-0 tablet-p:w-[calc(100%-15rem)] tablet-p:ml-60 border-t border-grey-90 bg-white">
              {state.status === SUBMITTAL_STATUS.PENDING_REVIEW && (
                <div className="flex items-center gap-2 py-4 px-8 max-phone:px-4 border-b border-grey-90">
                  <InfoIcon className="shrink-0 w-4 h-4 text-grey-50" />
                  <span className="font-avenir-medium type-body-2 text-grey-40">
                    By clicking &ldquo;Approve&rdquo;, you consent to the automated sharing of this
                    data element to the intended recipient.
                  </span>
                </div>
              )}
              <div
                className={clsx(
                  'flex pt-5 px-8 pb-4 max-phone:flex-wrap max-phone:gap-2 max-phone:py-3 max-phone:px-4',
                  state.status !== SUBMITTAL_STATUS.PENDING_REVIEW && 'max-phone:hidden'
                )}
              >
                {/* Revealed on the wide shell only. It is a second way to do
                    what the header's back chevron and breadcrumbs already do, and
                    900px is where this bar's own width switches below — so the bar
                    changes shape once rather than twice. */}
                <div className="hidden tablet-p:flex items-center">
                  <Button
                    data-testid="submittal-view-other-btn"
                    noborder
                    sizing="Medium"
                    disabled={actionState.submitting}
                    onClick={() => navigate(backTo)}
                  >
                    <ChevronLeft />
                    View Other Submissions
                  </Button>
                </div>
                {state.status === SUBMITTAL_STATUS.PENDING_REVIEW && (
                  <div className="flex items-center gap-3 ml-auto max-phone:ml-0 max-phone:w-full max-phone:justify-between">
                    <Button
                      data-testid="submittal-reject-btn"
                      sizing="Medium"
                      round
                      error
                      disabled={
                        actionState.submitting || state.status !== SUBMITTAL_STATUS.PENDING_REVIEW
                      }
                      onClick={handleOpenReject}
                      addClass="phone:min-w-[160px] phone:max-w-[160px] w-full"
                    >
                      Reject
                    </Button>
                    <Button
                      data-testid="submittal-approve-btn"
                      sizing="Medium"
                      round
                      disabled={
                        actionState.submitting || state.status !== SUBMITTAL_STATUS.PENDING_REVIEW
                      }
                      onClick={showApproveModal}
                      addClass="phone:min-w-[160px] phone:max-w-[160px] w-full"
                    >
                      Approve
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <ApproveModal
        visible={approveModalVisible}
        onClose={hideApproveModal}
        onConfirm={handleApprove}
        elementName={state.listingLabel}
        secondaryApproval={secondaryApproval}
      />
      <RejectModal
        visible={rejectModalVisible}
        onClose={hideRejectModal}
        onConfirm={handleReject}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
        secondaryApproval={secondaryApproval}
      />
    </div>
  )
}
